/* GrowthOS — Campaign record page.
   Rebuilt to mirror the Lead/Contact/Account/Opportunity detail (`.gos-leadrec`
   shell): a back row, header card (initials avatar, name, dots menu, chips), a
   horizontal tab bar and a read-only Overview + sticky Details panel. Replaces the
   old left-nav scroll-spy + `CampaignForm` (view mode) — same reasoning that applied
   to the other four detail pages (now that campaignForm.jsx is a plain editable form
   with no view-mode rendering, view pages can't reuse it anyway).
   Campaign-only capabilities preserved exactly (same APIs as the legacy page, moved
   into the header): **Send Now** (`sendNow`) as the primary action, **Stop** and
   **Refresh Status** in the dots menu (`stopORStartCampaign` / a `campaignList`
   refetch), plus **Edit Campaign**. Member tabs — Leads / Contacts (themed
   `CampaignMembersTab`, wired to getLeadsByCampaignId / getContactsByCampaignId) and
   Opportunities (the shared themed `sharedChild/opportunity/opportunities.jsx`, wired
   to getOpportunityByModule) — replace the legacy unstyled ChildLead/ChildContact/
   ChildDeal grids on this page. Tags (`CampaignTagEditor`) are wired up here for the
   first time: the legacy page already dispatched fetchTagsAndAppendToModule for
   'Campaign' but never rendered a Tags UI (dead-but-fetched, same as the pre-fix Deal
   page); reuses the exact generic Campaign tag APIs. No Emails tab — the legacy page
   deliberately commented out its email fetch (no per-record campaign email UI). */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  fetchAttachmentsAndAppendToModule,
  fetchDataAndUpdateStore,
  fetchDetailAndAppendToModule,
  fetchNotesAndAppendToModule,
  fetchTagsAndAppendToModule,
} from '../../redux/actions';
import { getCampaignById, getTagsByModule } from '../../redux/selector';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import apiServices from '../../services/service';
import { campaignInputData } from '../../shared/constants';
import Loader from '../../shared/component/Loader';
import Notes from '../sharedChild/note/notes';
import Attachments from '../sharedChild/attachment/attachments';
import Opportunities from '../sharedChild/opportunity/opportunities';
import Timeline from '../sharedChild/timeline';
import CampaignMembersTab from './CampaignMembersTab';

/* ---------- GrowthOS record icons (stroke, 24×24) ---------- */
const ICONS = {
  grid: <path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" />,
  clock: (<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
  note: <path d="M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5ZM8 13h8M8 17h5" />,
  paperclip: <path d="M21 8l-9 9a5 5 0 0 1-7-7l9-9a3.5 3.5 0 0 1 5 5l-9 9a2 2 0 0 1-3-3l8-8" />,
  campaigns: <path d="M3 11v2a1 1 0 0 0 1 1h2l4 4V6L6 10H4a1 1 0 0 0-1 1ZM15 8a4 4 0 0 1 0 8M18 5a8 8 0 0 1 0 14" />,
  mail: <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2ZM22 7l-10 6L2 7" />,
  send: <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7Z" />,
  stop: (<><rect x="4" y="4" width="16" height="16" rx="2" /><rect x="9" y="9" width="6" height="6" /></>),
  refresh: <path d="M21 12a9 9 0 1 1-3-6.7L21 8M21 3v5h-5" />,
  users: (<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11" /></>),
  contacts: (<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11" /></>),
  opportunity: <path d="M3 3v18h18M7 14l3-3 4 4 5-6" />,
  calendar: <path d="M8 2v4M16 2v4M3 9h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />,
  info: (<><circle cx="12" cy="12" r="9" /><path d="M12 16v-4M12 8h.01" /></>),
  template: <path d="M4 4h16a1 1 0 0 1 1 1v4H3V5a1 1 0 0 1 1-1ZM3 9h7v11H4a1 1 0 0 1-1-1V9ZM10 9h11v6H10zM10 15h11v4a1 1 0 0 1-1 1H10v-5Z" />,
  briefcase: <path d="M3 7h18v13H3zM8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />,
  dots: <path d="M12 5h.01M12 12h.01M12 19h.01" />,
  chevL: <path d="M15 18l-6-6 6-6" />,
  edit: <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />,
  x: <path d="M18 6 6 18M6 6l12 12" />,
  tag: (<><path d="M20.6 13.4 11 3.8a2 2 0 0 0-1.4-.6H4a1 1 0 0 0-1 1v5.6a2 2 0 0 0 .6 1.4l9.6 9.6a2 2 0 0 0 2.8 0l4.6-4.6a2 2 0 0 0 0-2.8z" /><path d="M7.5 7.5h.01" /></>),
  user: (<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>),
  plus: <path d="M12 5v14M5 12h14" />,
  search: (<><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>),
  check: <path d="M20 6 9 17l-5-5" />,
};
const Ic = ({ name, size = 16, className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {ICONS[name] || null}
  </svg>
);

const GOS_AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const gosInitials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase() || '?';
const gosAvaColor = (name) =>
  GOS_AVA_COLORS[String(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % GOS_AVA_COLORS.length];

const val = (v) => (v === 0 || v ? v : null);
const fmtDT = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// campaignStatus is admin-configurable master data — infer a tone by substring
// (inactive before active, since it's a superset match), matching the list grid.
const statusTone = (label) => {
  const s = String(label || '').toLowerCase();
  if (s.includes('inactive')) return 'red';
  if (s.includes('active')) return 'green';
  if (s.includes('complet')) return 'gray';
  if (s.includes('schedul')) return 'blue';
  if (s.includes('pause')) return 'amber';
  return 'gray';
};

// campaign-member API responses aren't a guaranteed shape — normalize defensively.
const asArray = (data, ...keys) => {
  if (Array.isArray(data)) return data;
  for (const k of keys) {
    if (Array.isArray(data?.[k])) return data[k];
  }
  return [];
};

const InfoCard = ({ title, icon, rows }) => (
  <div className="gos-lr-card">
    <div className="gos-lr-card-head">
      <div className="gos-lr-card-title">
        <Ic name={icon} size={16} /> {title}
      </div>
    </div>
    <div className="gos-lr-card-rows">
      {rows.map(([label, value], i) => (
        <div className="gos-lr-drow" key={i}>
          <div className="gos-lr-dlabel">{label}</div>
          <div className={'gos-lr-dval' + (val(value) ? '' : ' muted')}>{val(value) ?? '—'}</div>
        </div>
      ))}
    </div>
  </div>
);
const DetailRow = ({ icon, label, children }) => (
  <div className="gos-lr-drow">
    <div className="gos-lr-dlabel">
      <Ic name={icon} size={15} /> {label}
    </div>
    <div className="gos-lr-dval gos-lr-dval-right">{children ?? '—'}</div>
  </div>
);
const Placeholder = ({ icon, title, text }) => (
  <div className="gos-lr-card gos-lr-placeholder">
    <div className="gos-lr-ph-ic">
      <Ic name={icon} size={22} />
    </div>
    <div className="gos-lr-ph-title">{title}</div>
    <div className="gos-lr-ph-text">{text}</div>
  </div>
);

/* ---------- Campaign tags editor (add / remove / create) ---------- */
const TAG_TONES = ['blue', 'green', 'amber', 'purple', 'rose', 'gray', 'red'];
const toneFor = (name) =>
  TAG_TONES[String(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % TAG_TONES.length];
const tagNameOf = (t) => (Array.isArray(t?.name) ? t.name.join(', ') : t?.name || '');

const CampaignTagEditor = ({ campaign }) => {
  const dispatch = useDispatch();
  const library = useSelector(getTagsByModule('campaignList')) || [];
  const applied = campaign?.tags || [];
  const moduleId = campaign?.id;

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    dispatch(fetchDataAndUpdateStore({ key: 'tagsByModule', inputData: 'Campaign', storeKey: 'campaignList' }));
  }, [dispatch]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQ('');
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQ('');
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [open]);

  const refreshTags = () => {
    dispatch(fetchTagsAndAppendToModule({ moduleName: 'Campaign', id: moduleId }));
    dispatch(fetchDataAndUpdateStore({ key: 'tagsByModule', inputData: 'Campaign', storeKey: 'campaignList' }));
  };

  const addTagByName = async (name) => {
    if (!moduleId || busy) return;
    setBusy(true);
    try {
      const res = await apiServices.createTags({ tagName: [name], module: 'Campaign', moduleId: [moduleId] });
      if (res.status === 200) {
        toast.success('Tag added successfully!', { position: 'top-right' });
        refreshTags();
      }
    } catch (err) {
      console.error('Error adding tag:', err);
      toast.error('Failed to add tag.', { position: 'top-right' });
    } finally {
      setBusy(false);
    }
  };

  const removeTag = async (tag) => {
    if (!moduleId || busy) return;
    setBusy(true);
    try {
      const res = await apiServices.deleteTagsByModuleId({ id: [tag.id], module: 'Campaign', moduleId: [moduleId] });
      if (res.status === 200) {
        toast.success('Tag removed successfully!', { position: 'top-right' });
        refreshTags();
      }
    } catch (err) {
      console.error('Error removing tag:', err);
      toast.error('Failed to remove tag.', { position: 'top-right' });
    } finally {
      setBusy(false);
    }
  };

  const toggleLibraryTag = (libTag) => {
    const name = tagNameOf(libTag);
    const existing = applied.find((a) => tagNameOf(a) === name);
    if (existing) removeTag(existing);
    else addTagByName(name);
  };

  const ql = q.trim();
  const filtered = ql ? library.filter((t) => tagNameOf(t).toLowerCase().includes(ql.toLowerCase())) : library;
  const exact = library.some((t) => tagNameOf(t).toLowerCase() === ql.toLowerCase());
  const nextTone = TAG_TONES[library.length % TAG_TONES.length];

  const create = () => {
    if (!ql || exact || busy) return;
    addTagByName(ql);
    setQ('');
    inputRef.current?.focus();
  };

  return (
    <div className="gos-lead-tags">
      <span className="gos-lead-tags-label">
        <Ic name="tag" size={13} /> Tags
      </span>
      {applied.map((t) => {
        const tone = toneFor(tagNameOf(t));
        return (
          <span
            key={t.id}
            className="gos-lead-tag"
            style={{ background: `var(--gos-${tone}-bg)`, color: `var(--gos-${tone}-fg)` }}
          >
            <span className="gos-lt-dot" />
            {tagNameOf(t)}
            <button type="button" className="gos-lt-x" title="Remove tag" onClick={() => removeTag(t)} disabled={busy}>
              <Ic name="x" size={13} />
            </button>
          </span>
        );
      })}
      <div className="gos-tag-add-wrap" ref={wrapRef}>
        <button type="button" className={'gos-tag-add' + (open ? ' on' : '')} onClick={() => setOpen((o) => !o)}>
          <Ic name="plus" size={14} /> Add tag
        </button>
        {open ? (
          <div className="gos-tag-pop">
            <div className="gos-tag-pop-search">
              <Ic name="search" size={14} />
              <input
                ref={inputRef}
                placeholder="Search or create…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && ql && !exact) create();
                }}
              />
            </div>
            <div className="gos-tag-pop-label">{ql ? 'Matching tags' : 'Apply tags'}</div>
            <div className="gos-tag-pop-list">
              {filtered.length === 0 && !ql ? <div className="gos-tag-pop-empty">No tags yet</div> : null}
              {filtered.map((t) => {
                const name = tagNameOf(t);
                const on = applied.some((a) => tagNameOf(a) === name);
                const tone = toneFor(name);
                return (
                  <button
                    type="button"
                    key={t.id}
                    className={'gos-tag-opt' + (on ? ' on' : '')}
                    onClick={() => toggleLibraryTag(t)}
                    disabled={busy}
                  >
                    <span className="gos-to-dot" style={{ background: `var(--gos-${tone}-fg)` }} />
                    {name}
                    <span className="gos-to-check">{on ? <Ic name="check" size={12} /> : null}</span>
                  </button>
                );
              })}
              {ql && filtered.length === 0 ? (
                <div className="gos-tag-pop-empty">No matches — create it below</div>
              ) : null}
            </div>
            {ql && !exact ? (
              <button type="button" className="gos-tag-create" onClick={create} disabled={busy}>
                <span
                  className="gos-tc-swatch"
                  style={{ background: `var(--gos-${nextTone}-bg)`, border: `1px solid var(--gos-${nextTone}-fg)` }}
                />
                Create <strong>{`“${ql}”`}</strong>
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const ViewCampaigns = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const stateData = useSelector((state) => state);
  const storeLoading = isLoadingByKey(stateData, 'campaignList');
  const [loading, setLoading] = useState(storeLoading);
  const [refreshView, setRefreshView] = useState(false);

  const [leadMembers, setLeadMembers] = useState([]);
  const [leadMembersLoading, setLeadMembersLoading] = useState(false);
  const [contactMembers, setContactMembers] = useState([]);
  const [contactMembersLoading, setContactMembersLoading] = useState(false);
  const [opportunityByModule, setOpportunityByModule] = useState({});

  const searchParams = new URLSearchParams(location.search);
  const campaignID = searchParams.get('id');

  let hasViewLeadPermission = true;
  let hasViewContactPermission = true;
  let hasViewOpportunityPermission = true;
  let hasUpdateLeadPermission = true;
  let hasUpdateContactPermission = true;
  let hasUpdateOpportunityPermission = true;
  const permLead = JSON.parse(localStorage.getItem('userUIControlPermission'))?.leadmenu;
  const permContact = JSON.parse(localStorage.getItem('userUIControlPermission'))?.contactmenu;
  const permOpportunity = JSON.parse(localStorage.getItem('userUIControlPermission'))?.opportunitymenu;
  if (permLead) {
    hasViewLeadPermission = permLead?.leadviewscreen ? permLead.leadviewscreen.canView : true;
    hasUpdateLeadPermission = permLead?.leadupdatescreen ? permLead.leadupdatescreen.canView : true;
  }
  if (permContact) {
    hasViewContactPermission = permContact?.contactviewscreen ? permContact.contactviewscreen.canView : true;
    hasUpdateContactPermission = permContact?.contactupdatescreen ? permContact.contactupdatescreen.canView : true;
  }
  if (permOpportunity) {
    hasViewOpportunityPermission = permOpportunity?.opportunityviewscreen ? permOpportunity.opportunityviewscreen.canView : true;
    hasUpdateOpportunityPermission = permOpportunity?.opportunityupdatescreen ? permOpportunity.opportunityupdatescreen.canView : true;
  }

  const [campaignLocalData, setCampaignLocalData] = useState(location.state || {});
  const campaignStoreData = useSelector((state) => getCampaignById(state, location.state?.id || campaignID));

  useEffect(() => {
    setCampaignLocalData((prev) => ({ ...prev, ...campaignStoreData }));
  }, [campaignStoreData]);

  const handleOpenEditForm = () => {
    navigate('/campaigns/editCampaign', { state: { ...campaignLocalData, type: 'view', from: 'view' } });
  };

  const stopCampaign = async () => {
    if (!campaignLocalData?.id) return;
    setLoading(true);
    const data = {
      id: campaignLocalData.id,
      brevoStatus: campaignLocalData?.campaignStatus === 'InActive' ? 'queued' : 'suspended',
    };
    try {
      const response = await apiServices.stopORStartCampaign(data);
      if (response.status === 204) {
        await dispatch(fetchDataAndUpdateStore({ key: 'campaignList', inputData: campaignInputData }));
        setRefreshView((p) => !p);
        toast.success('Campaign Stopped Successfully!', { position: 'top-right' });
      }
    } catch (error) {
      console.error('Error while stopping campaign:', error);
      toast.error('Failed to stop the campaign.', { position: 'top-right' });
    } finally {
      setLoading(false);
    }
  };

  const sendNow = async () => {
    if (!campaignLocalData?.id) return;
    setLoading(true);
    try {
      const response = await apiServices.sendNow({ id: campaignLocalData.id });
      if (response.status === 200) {
        await dispatch(fetchDataAndUpdateStore({ key: 'campaignList', inputData: campaignInputData }));
        setRefreshView((p) => !p);
        toast.success('Campaign Sent Successfully', { position: 'top-right' });
      }
    } catch (error) {
      console.error('Error while sending campaign:', error);
      toast.error('Failed to send the campaign.', { position: 'top-right' });
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      await dispatch(fetchDataAndUpdateStore({ key: 'campaignList', inputData: campaignInputData }));
      setRefreshView((p) => !p);
    } catch (error) {
      console.error('Error refreshing campaign:', error);
      toast.error('Failed to refresh campaign status.', { position: 'top-right' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  useEffect(() => {
    const campaignId = location.state?.id || campaignID;
    if (campaignId) {
      dispatch(fetchDetailAndAppendToModule({ moduleName: 'Campaign', id: campaignId, localDetail: location.state || null }));
      dispatch(fetchNotesAndAppendToModule({ moduleName: 'Campaign', id: campaignId }));
      dispatch(fetchAttachmentsAndAppendToModule({ moduleName: 'Campaign', id: campaignId }));
      dispatch(fetchTagsAndAppendToModule({ moduleName: 'Campaign', id: campaignId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, campaignLocalData?.id, refreshView]);

  // member lists (Leads / Contacts / Opportunities) — fetched here so the tab-bar
  // count badges and the tab cards share one API call each.
  useEffect(() => {
    const id = campaignLocalData?.id;
    if (!id) return;
    setLeadMembersLoading(true);
    apiServices
      .getLeadsByCampaignId({ id })
      .then((res) => setLeadMembers(res.status === 200 ? asArray(res.data, 'leads', 'lead') : []))
      .catch((err) => console.error('Error fetching campaign leads:', err))
      .finally(() => setLeadMembersLoading(false));

    setContactMembersLoading(true);
    apiServices
      .getContactsByCampaignId({ id })
      .then((res) => setContactMembers(res.status === 200 ? asArray(res.data, 'contacts', 'contact') : []))
      .catch((err) => console.error('Error fetching campaign contacts:', err))
      .finally(() => setContactMembersLoading(false));

    apiServices
      .getOpportunityByModule({ moduleName: 'Campaign', id })
      .then((res) => res.status === 200 && setOpportunityByModule(res.data))
      .catch((err) => console.error('Error fetching campaign opportunities:', err));
  }, [campaignLocalData?.id, refreshView]);

  const cd = campaignLocalData || {};
  const name = cd.campaignName || 'Campaign';
  const status = cd.campaignStatus;
  const scheduleStr = fmtDT(cd.scheduledDateTime);
  const scheduledFuture = cd.scheduledDateTime && new Date() < new Date(cd.scheduledDateTime);
  const canSend = !!cd.id && status !== 'Complete' && status !== 'InActive';
  const oppList = opportunityByModule?.opportunity || [];

  const chips = [
    cd.campaignType && { icon: 'campaigns', label: cd.campaignType },
    status && { icon: 'info', label: status },
    cd.owner && { icon: 'user', label: cd.owner },
    scheduleStr && { icon: 'calendar', label: scheduleStr },
  ].filter(Boolean);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'grid' },
    { id: 'activities', label: 'Activities', icon: 'clock' },
    { id: 'notes', label: 'Notes', icon: 'note', count: cd.notesList?.length },
    { id: 'attachments', label: 'Attachments', icon: 'paperclip', count: cd.attachments?.length },
    hasViewLeadPermission && { id: 'leads', label: 'Leads', icon: 'users', count: leadMembers.length },
    hasViewContactPermission && { id: 'contacts', label: 'Contacts', icon: 'contacts', count: contactMembers.length },
    hasViewOpportunityPermission && { id: 'opportunities', label: 'Opportunities', icon: 'opportunity', count: oppList.length },
  ].filter(Boolean);

  const overview = (
    <div className="gos-lr-grid2">
      <InfoCard
        title="Campaign Information"
        icon="campaigns"
        rows={[
          ['Campaign Name', val(name)],
          ['Type', val(cd.campaignType)],
          ['Status', val(status)],
          ['Campaign For', val(cd.campaignFor)],
        ]}
      />
      <InfoCard
        title="Delivery & Schedule"
        icon="mail"
        rows={[
          ['Sender', val(cd.senderName || cd.senderEmail)],
          ['Template', val(cd.templateName)],
          ['Schedule At', val(scheduleStr)],
          ['Owner', val(cd.owner)],
        ]}
      />
      <InfoCard
        title="Record"
        icon="info"
        rows={[
          ['Created By', val(cd.createdBy)],
          ['Updated By', val(cd.updatedBy)],
          ['Created Date', val(fmtDT(cd.createdDateTime))],
        ]}
      />
      <InfoCard
        title="Description"
        icon="note"
        rows={[['Description', val(cd.description)]]}
      />
    </div>
  );

  const detailsPanel = (
    <div className="gos-lr-card gos-lr-sticky">
      <div className="gos-lr-card-head">
        <div className="gos-lr-card-title">
          <Ic name="info" size={16} /> Details
        </div>
        <button type="button" className="gos-lr-iconbtn" onClick={handleOpenEditForm} title="Edit campaign">
          <Ic name="edit" size={14} />
        </button>
      </div>
      <div className="gos-lr-sec">
        <div className="gos-lr-sec-title">Campaign</div>
        <div className="gos-lr-card-rows">
          <DetailRow icon="campaigns" label="Name">{val(name)}</DetailRow>
          <DetailRow icon="briefcase" label="Type">{val(cd.campaignType)}</DetailRow>
          <DetailRow icon="info" label="Status">
            {status ? (
              <span className={'gos-badge gos-b-' + statusTone(status)}>
                <span className="gos-pip" />
                {status}
              </span>
            ) : (
              '—'
            )}
          </DetailRow>
          <DetailRow icon="user" label="Owner">{val(cd.owner)}</DetailRow>
        </div>
      </div>
      <div className="gos-lr-divider" />
      <div className="gos-lr-sec">
        <div className="gos-lr-sec-title">Delivery</div>
        <div className="gos-lr-card-rows">
          <DetailRow icon="mail" label="Sender">{val(cd.senderName || cd.senderEmail)}</DetailRow>
          <DetailRow icon="template" label="Template">{val(cd.templateName)}</DetailRow>
          <DetailRow icon="calendar" label="Schedule">{val(scheduleStr)}</DetailRow>
        </div>
      </div>
    </div>
  );

  let tabBody;
  if (activeTab === 'overview') tabBody = overview;
  else if (activeTab === 'activities') tabBody = <Timeline localData={campaignLocalData} module={'Campaign'} />;
  else if (activeTab === 'notes') tabBody = <Notes leadLocalData={campaignLocalData} module={'Campaign'} />;
  else if (activeTab === 'attachments') tabBody = <Attachments localData={campaignLocalData} module={'Campaign'} />;
  else if (activeTab === 'leads')
    tabBody = hasViewLeadPermission ? (
      <CampaignMembersTab kind="lead" items={leadMembers} loading={leadMembersLoading} canAdd={hasUpdateLeadPermission} />
    ) : (
      <Placeholder icon="users" title="Leads" text="You don't have permission to view leads for this campaign." />
    );
  else if (activeTab === 'contacts')
    tabBody = hasViewContactPermission ? (
      <CampaignMembersTab kind="contact" items={contactMembers} loading={contactMembersLoading} canAdd={hasUpdateContactPermission} />
    ) : (
      <Placeholder icon="contacts" title="Contacts" text="You don't have permission to view contacts for this campaign." />
    );
  else if (activeTab === 'opportunities')
    tabBody = hasViewOpportunityPermission ? (
      <Opportunities localData={opportunityByModule} module={'Campaign'} hasUpdateOpportunityPermission={hasUpdateOpportunityPermission} />
    ) : (
      <Placeholder icon="opportunity" title="Opportunities" text="You don't have permission to view opportunities for this campaign." />
    );

  return (
    <>
      {loading && <Loader />}

      <div className="ViewLeadPageOuter gos-leadrec">
        {/* back row */}
        <div className="gos-lr-backrow">
          <button type="button" className="gos-lr-ghost-btn" onClick={() => navigate('/campaigns')}>
            <Ic name="chevL" size={15} /> Campaigns list
          </button>
          {cd.id ? (
            <div className="gos-lr-leadid">
              Campaign <span>{cd.id}</span>
            </div>
          ) : null}
        </div>

        {/* header card */}
        <div className="gos-lr-card gos-lr-header">
          <div className="gos-lr-header-top">
            <div className="gos-lr-header-l">
              <span className="gos-lr-ava" style={{ background: gosAvaColor(name) }}>
                {gosInitials(name)}
              </span>
              <div className="gos-lr-header-meta">
                <div className="gos-lr-name">{name}</div>
                <div className="gos-lr-subline">
                  <Ic name="campaigns" size={14} />
                  {[cd.campaignType, status].filter(Boolean).join(' · ') || '—'}
                  {scheduleStr ? (
                    <>
                      <span className="gos-lr-dot">·</span>
                      <Ic name="calendar" size={14} />
                      {scheduleStr}
                    </>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="gos-lr-actions">
              <button type="button" className="gos-lr-contact-btn" onClick={sendNow} disabled={!canSend}>
                <Ic name="send" size={16} /> Send Now
              </button>
              <button
                type="button"
                className="gos-lr-iconbtn gos-lr-iconbtn-bordered"
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="More actions"
              >
                <Ic name="dots" size={18} />
              </button>
              {menuOpen ? (
                <>
                  <div className="gos-lr-menu-backdrop" onClick={() => setMenuOpen(false)} />
                  <div className="gos-lr-menu">
                    <button type="button" className="gos-lr-menu-item" onClick={() => { setMenuOpen(false); handleOpenEditForm(); }}>
                      <Ic name="edit" size={15} /> Edit Campaign
                    </button>
                    {scheduledFuture ? (
                      <button
                        type="button"
                        className="gos-lr-menu-item"
                        onClick={() => { setMenuOpen(false); stopCampaign(); }}
                        disabled={status === 'InActive'}
                      >
                        <Ic name="stop" size={15} /> {status === 'InActive' ? 'Stopped' : 'Stop Campaign'}
                      </button>
                    ) : null}
                    <button type="button" className="gos-lr-menu-item" onClick={() => { setMenuOpen(false); refresh(); }}>
                      <Ic name="refresh" size={15} /> Refresh Status
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
          {chips.length ? (
            <div className="gos-lr-chips">
              {chips.map((c, i) => (
                <span className="gos-lr-chip" key={i}>
                  <Ic name={c.icon} size={14} /> {c.label}
                </span>
              ))}
            </div>
          ) : null}
          <CampaignTagEditor campaign={cd} />
        </div>

        {/* tabs */}
        <div className="gos-lr-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              className={'gos-lr-tab' + (activeTab === t.id ? ' active' : '')}
              onClick={() => setActiveTab(t.id)}
            >
              <Ic name={t.icon} size={15} /> {t.label}
              {t.count != null ? <span className="gos-lr-count">{t.count}</span> : null}
            </button>
          ))}
        </div>

        {/* body */}
        {activeTab === 'overview' ? (
          overview
        ) : (
          <div className="gos-lr-2col">
            <div className="gos-lr-main">{tabBody}</div>
            {detailsPanel}
          </div>
        )}
      </div>
    </>
  );
};

export default ViewCampaigns;
